/*
 * Module permission specs — how a module declares what it can authorize.
 *
 * The OS does not mandate the access levels a module may offer; each module owns its
 * own vocabulary and its own tiers. That keeps this contract package free of any
 * knowledge of which modules exist (it used to enumerate them, which is the coupling
 * Phase 2's capability boundary needs gone).
 *
 * Two properties are load-bearing:
 *
 *  1. **Permissions are keyed by ACTION; the wire string is derived** as
 *     `<module>:<action>`. Nothing anywhere hand-writes a permission string, so the
 *     naming convention holds by construction rather than by discipline.
 *
 *  2. **Levels are cumulative and expanded at grant time.** A stored grant holds the
 *     fully-expanded permission strings, so `resolveEffectivePermissions`,
 *     `hasAllPermissions`, `canAccessModule` and `requirePermissions` all keep working
 *     on a flat set — there is no level-expansion layer running on the auth path.
 *
 * A permission that appears in no level is "off-ladder" (e.g. `quoting:approve`, which
 * sits between Edit and Manage but isn't more of either). That is DERIVED, not flagged,
 * so there is no second list to keep in sync.
 */

export interface PermissionMeta {
  /** Human label, shown wherever this permission is rendered individually. */
  label: string;
  description?: string;
}

/** A named, cumulative bundle of one module's permissions. */
export interface AccessLevel {
  key: string;
  label: string;
  /** Fully-qualified permission strings, cumulative of every lower level. */
  grants: readonly string[];
}

/**
 * The erased shape, for code that handles many modules (the seed, the admin API, the
 * access UI). Deliberately WITHOUT `p`: adding an index signature there would let
 * `SPEC.p.typoed` type-check, defeating the whole point. Module code holds the
 * concrete `ModulePermissions<P>` and gets exact keys.
 */
export interface ModulePermissionSpec {
  readonly id: string;
  readonly name: string;
  /** Icon key resolved through the shell's static registry. */
  readonly icon: string;
  /** Every permission this module owns, fully qualified. */
  readonly all: readonly string[];
  /** Metadata keyed by fully-qualified string, for labels in the UI. */
  readonly meta: Readonly<Record<string, PermissionMeta>>;
  /** The floor to see the module at all — becomes the module row's requiredPermissions. */
  readonly visibilityPermission: string;
  readonly levels: readonly AccessLevel[];
  /** Permissions belonging to no level. Derived. */
  readonly offLadder: readonly string[];
}

export type ModulePermissions<P extends Record<string, PermissionMeta>> =
  ModulePermissionSpec & {
    /** Action key → fully-qualified permission string. */
    readonly p: { readonly [K in keyof P]: string };
  };

interface AccessLevelInput<K extends string> {
  key: string;
  label: string;
  grants: readonly K[];
}

interface ModulePermissionsInput<P extends Record<string, PermissionMeta>> {
  id: string;
  name: string;
  icon: string;
  /** The module's entire vocabulary — the one place a permission comes into existence. */
  permissions: P;
  visibility: keyof P & string;
  /** Ascending; each level must be a superset of the one below it. */
  levels: readonly AccessLevelInput<keyof P & string>[];
}

const qualify = (moduleId: string, action: string) => `${moduleId}:${action}`;

/**
 * Declare a module's permissions.
 *
 * `grants` and `visibility` are typed as `keyof P`, so a typo is a compile error rather
 * than a runtime surprise. The runtime checks below cover what types cannot: the
 * ordering invariant, and callers reaching this from plain JS.
 *
 * Validation throws at import time, which means it fails in CI — every spec is imported
 * by its own module's tests. The check that earns its keep is the visibility one: a
 * `manage` level that forgot to include `view` produces someone holding
 * `<module>:manage` who cannot SEE the module, because visibility gates on
 * `<module>:view`. That is maddening to debug from behaviour.
 */
export function defineModulePermissions<const P extends Record<string, PermissionMeta>>(
  input: ModulePermissionsInput<P>,
): ModulePermissions<P> {
  const { id, name, icon, permissions, visibility, levels } = input;
  const fail = (message: string): never => {
    throw new Error(`Invalid permission spec for module "${id}": ${message}`);
  };

  const actions = Object.keys(permissions);
  if (actions.length === 0) fail('declares no permissions');
  if (levels.length === 0) fail('declares no access levels');

  const p = Object.fromEntries(actions.map((a) => [a, qualify(id, a)])) as {
    readonly [K in keyof P]: string;
  };
  const meta = Object.fromEntries(
    actions.map((a) => [qualify(id, a), permissions[a] as PermissionMeta]),
  );
  const all = actions.map((a) => qualify(id, a));

  const seenLevelKeys = new Set<string>();
  const resolvedLevels: AccessLevel[] = [];
  let previous: Set<string> | null = null;

  for (const level of levels) {
    if (seenLevelKeys.has(level.key)) fail(`duplicate level key "${level.key}"`);
    seenLevelKeys.add(level.key);
    if (level.grants.length === 0) fail(`level "${level.key}" grants nothing`);

    for (const action of level.grants) {
      if (!Object.hasOwn(permissions, action)) {
        fail(`level "${level.key}" references undeclared permission "${String(action)}"`);
      }
    }

    const grantSet = new Set(level.grants.map((a) => qualify(id, a)));
    if (previous !== null) {
      const missing = [...previous].filter((g) => !grantSet.has(g));
      if (missing.length > 0) {
        fail(
          `level "${level.key}" is not a superset of the level below it — missing ` +
            `${missing.join(', ')}. Levels are cumulative and must be listed ascending.`,
        );
      }
    }
    previous = grantSet;
    resolvedLevels.push({ key: level.key, label: level.label, grants: [...grantSet] });
  }

  const visibilityPermission = qualify(id, visibility);
  const lowest = resolvedLevels[0]!;
  if (!lowest.grants.includes(visibilityPermission)) {
    fail(
      `visibility permission "${visibilityPermission}" is not granted by the lowest level ` +
        `("${lowest.key}"), so holders of that level could not see the module`,
    );
  }

  const inSomeLevel = new Set(resolvedLevels.flatMap((l) => l.grants));
  const offLadder = all.filter((perm) => !inSomeLevel.has(perm));

  return { id, name, icon, p, all, meta, visibilityPermission, levels: resolvedLevels, offLadder };
}

/** The permission strings a level grants, or null if the module has no such level. */
export function expandLevel(spec: ModulePermissionSpec, levelKey: string): string[] | null {
  const level = spec.levels.find((l) => l.key === levelKey);
  return level === undefined ? null : [...level.grants];
}

export interface ModuleAccess {
  /** Matching level key, or null for no access at all. */
  level: string | null;
  /** Held off-ladder permissions (rendered as checkboxes). */
  extras: string[];
  /** True when the held ladder permissions match no level exactly. */
  custom: boolean;
}

/**
 * Describe what a held permission set means for one module — the inverse of
 * `composeModuleGrant`, used to render the access UI.
 *
 * `custom` exists because a grant can hold a combination no level produces (hand-edited,
 * or written before a level definition changed). Showing "Custom" is honest; silently
 * rounding to the nearest level would misrepresent what someone can do.
 */
export function describeModuleAccess(
  spec: ModulePermissionSpec,
  held: Iterable<string>,
): ModuleAccess {
  const heldSet = new Set(held);
  const offLadder = new Set(spec.offLadder);
  const mine = spec.all.filter((perm) => heldSet.has(perm));
  const extras = mine.filter((perm) => offLadder.has(perm));
  const ladder = new Set(mine.filter((perm) => !offLadder.has(perm)));

  if (ladder.size === 0) return { level: null, extras, custom: false };

  const match = spec.levels.find(
    (l) => l.grants.length === ladder.size && l.grants.every((g) => ladder.has(g)),
  );
  return match === undefined
    ? { level: null, extras, custom: true }
    : { level: match.key, extras, custom: false };
}

/**
 * Build the permission strings to store for a module, from a chosen level plus
 * off-ladder extras. Returns null when the input names something this module doesn't
 * declare — the caller turns that into a 400 rather than storing nonsense.
 */
export function composeModuleGrant(
  spec: ModulePermissionSpec,
  levelKey: string | null,
  extras: readonly string[] = [],
): string[] | null {
  const out = new Set<string>();
  if (levelKey !== null) {
    const expanded = expandLevel(spec, levelKey);
    if (expanded === null) return null;
    for (const perm of expanded) out.add(perm);
  }
  for (const extra of extras) {
    if (!spec.offLadder.includes(extra)) return null;
    out.add(extra);
  }
  return [...out];
}
